<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { getAdminAuditEvents } from '../../api/reportApi.js'
import AppAlert from '../../shared/components/AppAlert.vue'
import AppPagination from '../../shared/components/AppPagination.vue'
import { formatHistoryDate } from '../history/historyUtils.js'
import {
  AUDIT_CATEGORIES,
  categoryLabel,
  describeActor,
  describeDetail,
  describeTarget,
  eventTypeLabel,
  eventTypesForCategory,
  outcomeLabel
} from './auditEventUtils.js'

const PAGE_SIZE = 20
const events = ref([])
const page = ref(0)
const totalElements = ref(0)
const totalPages = ref(0)
const isLoading = ref(false)
const error = ref('')
const category = ref('')
const eventType = ref('')
const eventTypeOptions = computed(() => eventTypesForCategory(category.value))

async function loadEvents(targetPage = 0) {
  isLoading.value = true
  error.value = ''
  try {
    const response = await getAdminAuditEvents({
      page: targetPage,
      size: PAGE_SIZE,
      category: category.value,
      eventType: eventType.value
    })
    events.value = response.content || []
    page.value = response.page
    totalElements.value = response.totalElements
    totalPages.value = response.totalPages
  } catch (cause) {
    error.value = cause.message || '감사 이력을 불러오지 못했습니다.'
  } finally {
    isLoading.value = false
  }
}

watch(category, () => {
  if (!eventTypeOptions.value.some((type) => type.value === eventType.value)) eventType.value = ''
})
watch([category, eventType], () => loadEvents(0))
onMounted(() => loadEvents(0))
</script>

<template>
  <section class="audit-history" aria-labelledby="audit-history-title">
    <div class="history-heading">
      <div>
        <p class="home-kicker">관리자 설정</p>
        <h1 id="audit-history-title">감사 이력</h1>
        <p class="page-lead">로그인·비밀번호 재설정 같은 인증 이력, 분석과 리포트 사용 이력, 관리자 작업 이력을 최신순으로 확인합니다.</p>
      </div>
      <div class="history-summary"><div><span>조회 결과</span><b>{{ totalElements }}건</b></div></div>
    </div>

    <div class="link-add audit-filters">
      <label>
        <span>분류</span>
        <select v-model="category" :disabled="isLoading">
          <option value="">전체</option>
          <option v-for="option in AUDIT_CATEGORIES" :key="option.value" :value="option.value">{{ option.label }}</option>
        </select>
      </label>
      <label class="link-add-coverage">
        <span>이벤트</span>
        <select v-model="eventType" :disabled="isLoading">
          <option value="">전체</option>
          <option v-for="option in eventTypeOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
        </select>
      </label>
    </div>

    <AppAlert v-if="error">{{ error }}</AppAlert>

    <div class="history-table-wrap">
      <table class="history-table audit-table">
        <thead>
          <tr>
            <th scope="col">일시</th>
            <th scope="col">분류</th>
            <th scope="col">이벤트</th>
            <th scope="col">결과</th>
            <th scope="col">사용자</th>
            <th scope="col">대상</th>
            <th scope="col">상세</th>
            <th scope="col">IP</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="isLoading"><td colspan="8" class="history-empty">불러오는 중입니다.</td></tr>
          <tr v-else-if="!events.length"><td colspan="8" class="history-empty">기록된 이력이 없습니다.</td></tr>
          <tr v-for="event in events" v-else :key="event.id">
            <td class="history-date">{{ formatHistoryDate(event.occurredAt) }}</td>
            <td>{{ categoryLabel(event.category) }}</td>
            <td><b>{{ eventTypeLabel(event.eventType) }}</b></td>
            <td><span class="audit-outcome" :class="`audit-outcome--${(event.outcome || '').toLowerCase()}`">{{ outcomeLabel(event.outcome) }}</span></td>
            <td>{{ describeActor(event) }}<small v-if="event.actorRole">{{ event.actorRole }}</small></td>
            <td class="audit-target">{{ describeTarget(event) }}</td>
            <td class="audit-detail">
              <span v-for="line in describeDetail(event)" :key="line">{{ line }}</span>
            </td>
            <td class="audit-ip">{{ event.clientIp || '-' }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <AppPagination
      :page="page"
      :total-pages="totalPages"
      :disabled="isLoading"
      @change="loadEvents"
    />
  </section>
</template>
